// src/pages/search/SearchPage.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, ShoppingBag, Star } from 'lucide-react';
import { getProducts } from '../../services/api';
import './SearchPage.css';

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  
  const [query, setQuery] = useState(initialQuery);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Update URL when the user submits the search
  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams({ q: query });
  };

  // Fetch all products on load
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await getProducts();
        if (data.success && data.products) {
          setProducts(data.products);
        }
      } catch (error) {
        console.error('Failed to load products for search:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Filter products when the URL query changes
  useEffect(() => {
    if (!initialQuery.trim()) {
      setFilteredProducts([]);
      return;
    }
    const term = initialQuery.toLowerCase();
    const results = products.filter(
      (p) =>
        (p.name && p.name.toLowerCase().includes(term)) ||
        (p.description && p.description.toLowerCase().includes(term)) ||
        (p.category && p.category.toLowerCase().includes(term))
    );
    setFilteredProducts(results);
  }, [initialQuery, products]);

  // Quick add to cart helper
  const addToCart = (product) => {
    const cart = JSON.parse(localStorage.getItem('cart')) || [];
    const existing = cart.find((item) => item.id === product.id);
    if (existing) {
      existing.quantity += 1;
    } else {
      cart.push({ ...product, quantity: 1 });
    }
    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));
    window.dispatchEvent(new Event('openSideCart'));
  };

  return (
    <div className="search-page">
      <div className="search-container">
        
        {/* Search Header & Input */}
        <div className="search-header">
          <h1 className="search-title">Search Our Store</h1>
          <form onSubmit={handleSearch} className="search-form">
            <Search className="search-form-icon" size={20} />
            <input
              type="text"
              placeholder="Search for products, categories, etc..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="search-input"
            />
            <button type="submit" className="search-submit-btn">
              Search
            </button>
          </form>
        </div>

        {/* Search Results */}
        {loading ? (
          <div className="search-loader">Loading products...</div>
        ) : !initialQuery ? (
          <div className="search-empty">
            <p>Type in the search bar above to find products.</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="search-empty">
            <h2>No results for "{initialQuery}"</h2>
            <p>Try checking your spelling or use more general terms.</p>
            <Link to="/shop" className="back-to-shop-btn">Back to Shop</Link>
          </div>
        ) : (
          <div className="search-results">
            <p className="results-count">
              Showing {filteredProducts.length} results for "{initialQuery}"
            </p>
            
            <div className="search-grid">
              {filteredProducts.map((product) => {
                const displayPrice = parseFloat(product.sale_price || product.price);
                return (
                  <div key={product.id} className="search-product-card">
                    <Link to={`/product/${product.id}`} className="search-product-link">
                      <div className="search-product-img-wrapper">
                        <img
                          src={product.image || '/assets/placeholder.jpg'}
                          alt={product.name}
                          className="search-product-img"
                        />
                      </div>
                      <p className="search-product-category">{product.category}</p>
                      <h3 className="search-product-name">{product.name}</h3>
                    </Link>
                    <div className="search-product-price">
                      PKR {displayPrice.toLocaleString()}
                    </div>
                    <button 
                      className="search-add-btn"
                      onClick={() => addToCart(product)}
                    >
                      <ShoppingBag size={16} /> Quick Add
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;